/**
 * Anchor highlights for in-doc ask (#04/#08). The passage the user asked about
 * stays visibly highlighted in the prose while the dock is open, so the answer
 * can be matched back to "this exact sentence"; each ask turn keeps its own
 * range (keyed) so a historical turn's 「引自」 chip can scroll back to ITS
 * passage (#08). Uses the CSS Custom Highlight API — no DOM mutation, so it
 * never fights React's ownership of the markdown-rendered tree. Styled by
 * `::highlight(guides-anchor)` in guides.css. No-ops gracefully where the API
 * is unavailable (e.g. jsdom).
 */

const ANCHOR = "guides-anchor";
const FLASH = "guides-anchor-flash";

interface HighlightRegistryLike {
  set(name: string, highlight: unknown): void;
  delete(name: string): void;
  has(name: string): boolean;
}
type HighlightCtor = new (...ranges: Range[]) => unknown;

function registry(): HighlightRegistryLike | null {
  const css = globalThis.CSS as unknown as { highlights?: HighlightRegistryLike } | undefined;
  return css?.highlights ?? null;
}
function highlightCtor(): HighlightCtor | null {
  const ctor = (globalThis as unknown as { Highlight?: HighlightCtor }).Highlight;
  return typeof ctor === "function" ? ctor : null;
}

/** One range per ask turn (keyed by the ask's monotonic key), bounded. */
const MAX_RANGES = 20;
const ranges = new Map<number, Range>();
let latestKey: number | null = null;
let flashTimer: ReturnType<typeof setTimeout> | null = null;

function paintLatest(): void {
  const reg = registry();
  const Ctor = highlightCtor();
  if (!reg || !Ctor) return;
  const range = latestKey !== null ? ranges.get(latestKey) : undefined;
  if (range?.startContainer.isConnected) reg.set(ANCHOR, new Ctor(range));
  else reg.delete(ANCHOR);
}

/** Pin the given selection range as the current anchor highlight (#04). */
export function setAnchorHighlight(range: Range, key = 0): void {
  ranges.set(key, range.cloneRange());
  latestKey = key;
  // Long conversations must not grow the map unboundedly — drop oldest first.
  for (const k of ranges.keys()) {
    if (ranges.size <= MAX_RANGES) break;
    ranges.delete(k);
  }
  paintLatest();
}

/** Drop everything (dock destroyed / page changed — DOM ranges are stale). */
export function clearAnchorHighlight(): void {
  const reg = registry();
  reg?.delete(ANCHOR);
  reg?.delete(FLASH);
  if (flashTimer) clearTimeout(flashTimer);
  flashTimer = null;
  ranges.clear();
  latestKey = null;
}

/** Dock hidden (1b: close = minimize): unpaint but KEEP the turn ranges. */
export function hideAnchorHighlight(): void {
  const reg = registry();
  reg?.delete(ANCHOR);
  reg?.delete(FLASH);
}

/** Dock re-shown: repaint the latest anchor if its DOM is still alive. */
export function showAnchorHighlight(): void {
  paintLatest();
}

/**
 * Scroll the prose back to an anchored passage and flash it briefly (#04:
 * clicking the dock's pinned quote / a turn's 「引自」 chip returns you to the
 * source sentence). No key → the latest ask. Returns false when the anchor is
 * gone (e.g. after page navigation).
 */
export function scrollToAnchorHighlight(key?: number): boolean {
  const k = key ?? latestKey;
  const range = k !== null && k !== undefined ? ranges.get(k) : undefined;
  const el = range?.startContainer?.parentElement;
  if (!range || !el?.isConnected) return false;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  const reg = registry();
  const Ctor = highlightCtor();
  if (reg && Ctor) {
    reg.set(FLASH, new Ctor(range.cloneRange()));
    if (flashTimer) clearTimeout(flashTimer);
    flashTimer = setTimeout(() => registry()?.delete(FLASH), 1500);
  }
  return true;
}
